import AddRounded from "@mui/icons-material/AddRounded";
import RemoveRounded from "@mui/icons-material/RemoveRounded";
import {
    Box,
    Button,
    IconButton,
    InputBase,
    Stack,
    Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PRODUCT_INFO } from "../../../locales/components/namespaces";

const fmtEUR = (n) =>
    new Intl.NumberFormat("el-GR", { style: "currency", currency: "EUR" })
        .format(Number(String(n || 0).replace(",", ".")))
        .replace(/\s/g, " ");

export default function ProductPriceActions({
                                                price = { current: 0, old: 0, vatLabel: "Χωρίς ΦΠΑ" },
                                                quantity = 1,
                                                onInc,
                                                onDec,
                                                onAdd,
                                                onQuantityChange,
                                                currentPriceFont = '"YourBrandBold", system-ui, sans-serif',
                                                oldPriceFont = "system-ui, sans-serif",
                                                product,
                                                below = null,
                                            }) {
    const { t } = useTranslation(PRODUCT_INFO);
    const showOld = Number(String(price.old || 0).replace(",", ".")) > 0;
    const [editValue, setEditValue] = useState(String(quantity));
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        setEditValue(String(quantity));
    }, [quantity]);

    const commitQuantity = (raw) => {
        const cleaned = String(raw).replace(/[^\d]/g, "");
        const parsed = Math.max(1, Number(cleaned || 1));

        setEditValue(String(parsed));
        setIsEditing(false);

        if (typeof onQuantityChange === "function") {
            onQuantityChange(parsed);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            commitQuantity(editValue);
        } else if (e.key === "Escape") {
            setEditValue(String(quantity));
            setIsEditing(false);
        }
    };

    const handleBlur = () => {
        commitQuantity(editValue);
    };

    const isUnavailable = String(product?.availability?.value) === "3";

    return (
        <Box
            sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                columnGap: { xs: 2, md: 1 },
                rowGap: 2,
                minWidth: 0,
                "& *": { minWidth: 0 },
            }}
        >
            {!isUnavailable && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                    <IconButton
                        size="small"
                        onClick={onDec}
                        sx={{ width: 36, height: 36, borderRadius: 2 }}
                    >
                        <RemoveRounded fontSize="small" />
                    </IconButton>

                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            bgcolor: "#fff",
                            border: "1px solid #E9E9E9",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 16,
                            fontWeight: 700,
                            lineHeight: 1,
                            userSelect: "none",
                            px: 0.5,
                        }}
                        onClick={() => setIsEditing(true)}
                    >
                        {isEditing ? (
                            <InputBase
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={handleKeyDown}
                                onBlur={handleBlur}
                                inputProps={{
                                    inputMode: "numeric",
                                    pattern: "[0-9]*",
                                    "aria-label": t("quantity", { ns: PRODUCT_INFO }),
                                    style: {
                                        textAlign: "center",
                                        fontSize: 16,
                                        fontWeight: 700,
                                        lineHeight: 1,
                                        width: "100%",
                                    },
                                }}
                                sx={{
                                    height: "100%",
                                    alignItems: "center",
                                    "& .MuiInputBase-input": {
                                        py: 0,
                                    },
                                }}
                                autoFocus
                            />
                        ) : (
                            <Typography
                                sx={{
                                    fontSize: 16,
                                    fontWeight: 700,
                                    lineHeight: 1,
                                    width: "100%",
                                    textAlign: "center",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {editValue}
                            </Typography>
                        )}
                    </Box>

                    <IconButton
                        size="small"
                        onClick={onInc}
                        sx={{ width: 36, height: 36, borderRadius: 2 }}
                    >
                        <AddRounded fontSize="small" />
                    </IconButton>
                </Box>
            )}

            <Box
                sx={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 1.25,
                    minWidth: 140,
                }}
            >
                {showOld && (
                    <Typography
                        sx={{
                            fontFamily: oldPriceFont,
                            fontSize: 14,
                            color: "#000",
                            textDecoration: "line-through",
                            textDecorationColor: "#000",
                            fontWeight: 500,
                            lineHeight: 1,
                            whiteSpace: "nowrap",
                        }}
                    >
                        {fmtEUR(price.old)}
                    </Typography>
                )}

                <Stack spacing={0.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontFamily: currentPriceFont,
                            fontSize: { xs: 26, sm: 28, md: 30 },
                            fontWeight: 600,
                            color: "#E5007E",
                            lineHeight: 1,
                            letterSpacing: 0.2,
                            whiteSpace: "nowrap",
                        }}
                    >
                        {fmtEUR(price.current)}
                    </Typography>

                    {!!price.vatLabel && (
                        <Typography
                            variant="caption"
                            sx={{ color: "#9E9E9E", lineHeight: 1, mt: "2px" }}
                        >
                            {price.vatLabel}
                        </Typography>
                    )}
                </Stack>
            </Box>

            <Box
                sx={{
                    flexShrink: 0,
                    ml: { xs: 1, sm: 2, md: 4 },
                }}
            >
                <Button
                    onClick={onAdd}
                    disabled={isUnavailable}
                    variant="contained"
                    sx={{
                        bgcolor: "#E5007E",
                        color: "#fff",
                        borderRadius: 999,
                        px: { xs: 2, sm: 2.5 },
                        height: 42,
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: { xs: 14.5, sm: 15 },
                        boxShadow: "none",
                        "&:hover": { bgcolor: "#c9006b", boxShadow: "none" },
                        whiteSpace: "nowrap",
                    }}
                >
                    {t('Add')}
                </Button>
            </Box>

            {!!below && (
                <Box
                    sx={{
                        flexBasis: "100%",
                        mt: 1,
                    }}
                >
                    {below}
                </Box>
            )}
        </Box>
    );
}